import React, { useState } from 'react';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { Typography, Button, showToast } from '@deeptrue-frontend/ui';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { useFormatMessage } from 'components/IntlProvider';
import SettingsMenu from '../../components/Settings/SettingsMenu';
import api from 'api/api';
import { BexioUserStatus } from 'store/modules/auth/auth';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2rem 1rem;
  width: 100%;
`;

const PaymentResign = styled.a`
  color: #144bb8;
  display: inline-block;
  text-decoration: none;
  font-weight: 600;
  margin: 1.5rem 0;
  font-size: 0.875rem;
`;

const StyledButton = styled(Button)`
  max-width: 20rem;
`;

const { Redirect } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

interface BexioInfoProps extends RouteComponentProps {
  isBexioUser?: boolean;
  bexioUserStatus: BexioUserStatus;
  bexioIntegrationUuid: string;
  updateBexioUser: ({ status }: { status: number }) => void;
}

const BexioInfo = ({
  location,
  isBexioUser,
  bexioUserStatus,
  bexioIntegrationUuid,
  updateBexioUser,
}: BexioInfoProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const t = useFormatMessage();

  if (!isBexioUser) {
    return <Redirect to="/settings/profile" />;
  }

  const suspendService = () => {
    setIsProcessing(true);
    api.resources.bexio
      .suspendAccount({ bexioUserUuid: bexioIntegrationUuid })
      .then(() => {
        updateBexioUser({ status: BexioUserStatus.SUSPENDED });
        setIsProcessing(false);
        showToast({
          type: 'success',
          title: t(
            'bexio.suspend.success',
            'You succesfully suspend your account'
          ),
        });
      })
      .catch(() => {
        setIsProcessing(false);
      });
  };

  const retrieveService = () => {
    setIsProcessing(true);
    api.resources.bexio
      .retrieveAccount({
        bexioUserUuid: bexioIntegrationUuid,
      })
      .then(({ data }) => {
        updateBexioUser({ status: data.status });
        setIsProcessing(false);
        showToast({
          type: 'success',
          title: t(
            'bexio.resign.success',
            'You succesfully retrieve your account'
          ),
        });
      })
      .catch(() => {
        setIsProcessing(false);
      });
  };

  const isAccountSuspended = bexioUserStatus === BexioUserStatus.SUSPENDED;

  return (
    <Dashboard
      title={t('profile.title', 'Settings')}
      location={location}
      headerTitle={t('bexio.info.title', 'Bexio')}
      headerSubtitle={t('bexio.info.desc', 'Manage your bexio account')}
    >
      <Grid item xs={12}>
        <SettingsMenu />
      </Grid>
      <Container>
        {isAccountSuspended && (
          <Typography mb="0.5rem" id="suspend-information">
            {t('bexio.info.resign.info', 'Your account is suspended')}
          </Typography>
        )}
        <StyledButton
          isLoading={isProcessing}
          id="suspend-retrieve"
          variant="primary"
          onClick={isAccountSuspended ? retrieveService : suspendService}
        >
          {isAccountSuspended
            ? t('bexio.settings.service.retrieve', 'Retrieve Service')
            : t('bexio.settings.service.suspend', 'Suspend Service')}
        </StyledButton>
        <Typography>
          <PaymentResign
            target="_blank"
            rel="noopener noreferrer"
            href="https://deeptrue.atlassian.net/servicedesk/customer/portal/1"
          >
            {t('bexio.info.resign', 'Want to resign from the service?')}
          </PaymentResign>
        </Typography>
      </Container>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state) => ({
      isBexioUser: selectors.authSelectors.isBexioUser(state),
      language: selectors.intlSelectors.language(state),
      bexioUserStatus: selectors.authSelectors.bexioUserStatus(state),
      bexioIntegrationUuid: selectors.authSelectors.bexioIntegrationUuid(state),
    }),
    {
      updateBexioUser: actions.authActions.bexio.updateBexioStatus,
    }
  )
)(BexioInfo);
